/**
 * EmptyStateV2 - Wave 0 (2026-05-27). Shared empty-state component
 * with optional illustration, primary CTA + secondary link. Used by
 * every new buildout page so empties feel intentional, not broken.
 */
import { type ComponentType, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { useMotionLevel } from '@/lib/motion-level';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

interface EmptyStateV2Props {
  icon?: ComponentType<{ className?: string }>;
  title: string;
  description?: ReactNode;
  primaryAction?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; href: string };
  className?: string;
}

export function EmptyStateV2({
  icon: Icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  className,
}: EmptyStateV2Props): JSX.Element {
  const level = useMotionLevel();
  return (
    <motion.div
      initial={level === 'full' ? { opacity: 0, y: 8 } : { opacity: 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        level === 'full'
          ? { type: 'spring', stiffness: 280, damping: 28 }
          : { duration: 0.18 }
      }
      className={cn(
        'glass-3d flex flex-col items-center justify-center px-8 py-12 text-center',
        className,
      )}
    >
      {Icon && (
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>
      )}
      <h3 className="text-sm font-semibold">{title}</h3>
      {description && (
        <p className="mt-2 max-w-md text-xs text-muted-foreground leading-relaxed">
          {description}
        </p>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {primaryAction && (
            <Button size="sm" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <a
              href={secondaryAction.href}
              className="text-xs text-primary underline underline-offset-2 hover:text-primary/80"
            >
              {secondaryAction.label}
            </a>
          )}
        </div>
      )}
    </motion.div>
  );
}
